/**
 * Timeline view (FS-058).
 *
 * One component for both sources, because to the person reading it there is
 * one timeline. An audit entry says what the system recorded; a contact entry
 * says what somebody did with a customer, and carries the detail that makes it
 * worth reading — outbound or inbound, how it went, how long it took.
 *
 * The entity route comes from the server's facets rather than a map in here,
 * so a new kind of history does not need a frontend change to be clickable.
 */

import { Link } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  ArrowRight,
  Check,
  Package,
  DollarSign,
  FileText,
  Globe,
  Phone,
  Mail,
  Users,
  MapPin,
  MessageSquare,
  StickyNote,
  CalendarClock,
  ShieldAlert,
  Undo2,
  Clock,
} from "lucide-react";
import type { HistoryEntry, HistoryKind } from "@/features/activity/api";

interface TimelineViewProps {
  entries: HistoryEntry[];
  /** Show which record each entry is about. Off on a record's own page. */
  showEntity?: boolean;
  /** From `useActivityFacets`; supplies labels and detail routes. */
  kinds?: HistoryKind[];
  /** The server's sentence describing what was counted. */
  counted?: string;
  emptyMessage?: string;
}

const icons: Record<string, React.ElementType> = {
  // Audit
  created: Plus,
  updated: Edit,
  deleted: Trash2,
  deactivated: Trash2,
  restored: Undo2,
  status_changed: ArrowRight,
  submitted: FileText,
  approved: Check,
  confirmed: Check,
  invoiced: FileText,
  received: Package,
  partially_received: Package,
  completed: Check,
  cancelled: Trash2,
  voided: Undo2,
  disputed: ShieldAlert,
  revised: Edit,
  converted: ArrowRight,
  payment_recorded: DollarSign,
  payment_voided: Undo2,
  credit_applied: DollarSign,
  credit_refunded: DollarSign,
  variance_accepted: Check,
  tax_updated: Edit,
  email_sent: Mail,
  permissions_changed: ShieldAlert,
  imported: Package,
  exported: Globe,
  // Contact
  call: Phone,
  email: Mail,
  meeting: Users,
  site_visit: MapPin,
  demo: Users,
  text: MessageSquare,
  note: StickyNote,
  follow_up: CalendarClock,
  trade_show: Globe,
  other: MessageSquare,
};

const tones: Record<string, string> = {
  created:
    "bg-[var(--fill-success)] text-[var(--text-success)] dark:bg-emerald-900/30 dark:text-emerald-300",
  approved:
    "bg-[var(--fill-success)] text-[var(--text-success)] dark:bg-emerald-900/30 dark:text-emerald-300",
  completed:
    "bg-[var(--fill-info)] text-[var(--color-interactive-primary)] dark:bg-blue-900/30 dark:text-blue-300",
  confirmed:
    "bg-[var(--fill-info)] text-[var(--color-interactive-primary)] dark:bg-blue-900/30 dark:text-blue-300",
  deleted:
    "bg-[var(--fill-danger)] text-[var(--text-danger)] dark:bg-red-900/30 dark:text-red-300",
  deactivated:
    "bg-[var(--fill-danger)] text-[var(--text-danger)] dark:bg-red-900/30 dark:text-red-300",
  voided:
    "bg-[var(--fill-danger)] text-[var(--text-danger)] dark:bg-red-900/30 dark:text-red-300",
  disputed:
    "bg-[var(--fill-warning)] text-[var(--text-warning)] dark:bg-amber-900/30 dark:text-amber-300",
  permissions_changed:
    "bg-[var(--fill-warning)] text-[var(--text-warning)] dark:bg-amber-900/30 dark:text-amber-300",
  payment_recorded:
    "bg-[var(--fill-success)] text-[var(--text-success)] dark:bg-emerald-900/30 dark:text-emerald-300",
};

// Contact entries read as one family, so they share a tone rather than each
// kind inventing a colour.
const CONTACT_TONE =
  "bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300";

const NEUTRAL_TONE =
  "bg-[var(--surface-sunken)] text-[var(--text-secondary)] dark:bg-gray-800 dark:text-gray-400";

/** Past-tense phrasing, so the line reads as a sentence about a person. */
const PHRASES: Record<string, string> = {
  created: "created",
  updated: "updated",
  deleted: "permanently deleted",
  deactivated: "deactivated",
  status_changed: "moved",
  partially_received: "partly received",
  payment_recorded: "took a payment on",
  payment_voided: "voided a payment on",
  credit_applied: "applied a credit to",
  credit_refunded: "recorded a refund on",
  variance_accepted: "accepted a variance on",
  permissions_changed: "changed permissions for",
  tax_updated: "overrode tax on",
  email_sent: "emailed",
  call: "called about",
  site_visit: "visited",
  follow_up: "followed up on",
  trade_show: "met at a trade show about",
  note: "noted on",
  text: "texted about",
  meeting: "met about",
  demo: "demonstrated",
  email: "emailed about",
};

function phraseFor(entry: HistoryEntry): string {
  return PHRASES[entry.action] ?? entry.action.replace(/_/g, " ");
}

function relativeTime(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 0) return new Date(dateStr).toLocaleDateString();
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/** Values worth surfacing from `extra_data`, in the order they read best. */
const CONTACT_DETAILS = ["direction", "outcome", "duration_minutes"] as const;

function contactDetail(entry: HistoryEntry): string | null {
  if (entry.source !== "contact" || !entry.extra_data) return null;
  const parts = CONTACT_DETAILS.map((key) => {
    const value = entry.extra_data?.[key];
    if (value === undefined || value === null) return null;
    if (key === "duration_minutes") return `${String(value)} min`;
    return String(value).replace(/_/g, " ");
  }).filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

function TimelineView({
  entries,
  showEntity = false,
  kinds,
  counted,
  emptyMessage = "No activity yet.",
}: TimelineViewProps) {
  const byKey = new Map((kinds ?? []).map((kind) => [kind.key, kind]));

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center py-8">
        <Clock className="h-8 w-8 text-[var(--text-tertiary)]" />
        <p className="mt-2 text-sm text-[var(--text-tertiary)]">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div>
      {counted && (
        <p className="border-b border-[var(--border-subtle)] px-3 pb-2 text-xs text-[var(--text-tertiary)]">
          {counted}
        </p>
      )}
      <ol className="divide-y divide-[var(--border-subtle)]">
        {entries.map((entry) => {
          const Icon = icons[entry.action] ?? Edit;
          const tone =
            entry.source === "contact"
              ? CONTACT_TONE
              : (tones[entry.action] ?? NEUTRAL_TONE);
          const kind = byKey.get(entry.entity_type);
          const route = kind?.route;
          const detail = contactDetail(entry);
          const notes =
            entry.source === "contact"
              ? ((entry.extra_data?.notes as string | undefined) ?? null)
              : null;

          return (
            <li
              key={`${entry.source}-${entry.id}`}
              className="flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-[var(--color-surface-hover)]"
            >
              <span
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${tone}`}
                aria-hidden="true"
              >
                <Icon className="h-3.5 w-3.5" />
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-sm text-[var(--text-primary)]">
                  <span className="font-medium">
                    {entry.actor_name || "System"}
                  </span>{" "}
                  <span className="text-[var(--text-secondary)]">
                    {phraseFor(entry)}
                  </span>
                  {showEntity && entry.entity_label && (
                    <>
                      {" "}
                      {route && entry.entity_id ? (
                        <Link
                          to={`${route}/${entry.entity_id}`}
                          className="font-medium text-brand-600 hover:text-brand-700"
                        >
                          {entry.entity_label}
                        </Link>
                      ) : (
                        <span className="font-medium">
                          {entry.entity_label}
                        </span>
                      )}
                    </>
                  )}
                  {showEntity && kind && (
                    <span className="ml-1.5 text-xs text-[var(--text-tertiary)]">
                      {kind.label}
                    </span>
                  )}
                </p>

                {!showEntity && entry.entity_label && (
                  <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
                    {entry.entity_label}
                  </p>
                )}

                {detail && (
                  <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">
                    {detail}
                  </p>
                )}

                {notes && (
                  <p className="mt-1 line-clamp-2 text-xs text-[var(--text-secondary)]">
                    {notes}
                  </p>
                )}

                {entry.changes && Object.keys(entry.changes).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {Object.entries(entry.changes).map(([field, value]) => {
                      const move = value as
                        | { from?: string | null; to?: string | null }
                        | undefined;
                      if (move?.from === undefined || move?.to === undefined) {
                        return null;
                      }
                      return (
                        <span
                          key={field}
                          className="inline-flex items-center gap-1 rounded-[var(--radius-full)] bg-[var(--color-surface-secondary)] px-2 py-0.5 text-xs text-[var(--text-secondary)]"
                        >
                          <span className="font-medium">
                            {field.replace(/_/g, " ")}
                          </span>
                          {move.from ?? "—"} → {move.to ?? "—"}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              <time
                dateTime={entry.occurred_at}
                title={new Date(entry.occurred_at).toLocaleString()}
                className="shrink-0 text-xs text-[var(--text-tertiary)]"
              >
                {relativeTime(entry.occurred_at)}
              </time>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default TimelineView;
